import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuShare2, LuMapPin, LuPlus } from 'react-icons/lu';
import api from '../../services/api';
import { estadoDe } from '../../utils/pedidosEmpresa';
import { getTodayISO } from '../../utils/dateUtils';
import { horaTexto } from '../../utils/ordenes';
import { FRANJAS as FRANJAS_TODAS } from '../../utils/franjas';

// Agenda del Portal Empresa (14-oct-2026): qué días vamos a sus clientes, en orden y con hora
// estimada. Solo para mirar: la ruta la arma Dispenser La Tienda. "Avisarle al cliente" arma el
// mensaje y abre el Compartir del celu: la app no guarda ni muestra números.
// "Tenés clientes cerca": clientes con el service vencido o por vencer cerca de las paradas del día.
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const DIA_ID = ['DOMINGO', 'LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO'];
const FRANJAS = FRANJAS_TODAS.map(f => f.id);
const partes = (iso) => { const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number); return { dow: new Date(a, m - 1, d).getDay(), d, m }; };
const diaLargo = (iso) => { const { dow, d, m } = partes(iso); return `${DIAS[dow]} ${d}/${m}`; };
const metros = (m) => (m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1).replace('.', ',')} km`);

function tituloDia(iso) {
    const hoy = getTodayISO();
    const manana = (() => { const [a, m, d] = hoy.split('-').map(Number); const x = new Date(a, m - 1, d + 1); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`; })();
    const base = diaLargo(iso);
    if (iso === hoy) return `Hoy · ${base}`;
    if (iso === manana) return `Mañana · ${base}`;
    return base.charAt(0).toUpperCase() + base.slice(1);
}

async function compartir(texto) {
    if (navigator.share) {
        try { await navigator.share({ text: texto }); return; } catch (e) { if (e?.name === 'AbortError') return; }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
}

const mensajeAviso = (v) => {
    // 26-oct-2026: la hora es una franja ("08:00-10:00"): "entre las 8 y las 10", no "alrededor de las 08:00-10:00"
    const r = /^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/.exec(String(v.hora || ''));
    const cuando = `${iso2txt(v.fecha)}${r ? ` entre las ${Number(r[1])} y las ${Number(r[3])}` : v.hora ? ` alrededor de las ${v.hora}` : ''}`;
    const que = v.tarea && v.tarea !== 'Visita' ? ` para ${v.tarea.toLowerCase()}` : '';
    return `Hola! Te aviso que el ${cuando} pasa el técnico de service${v.lugar ? ` por ${v.lugar}` : ''}${v.direccion ? ` (${v.direccion})` : ''}${que}. `
        + 'Si ese día no hay nadie para recibirlo, avisame así lo reprogramamos.';
};
const iso2txt = (iso) => (iso === getTodayISO() ? `día de hoy, ${diaLargo(iso)}` : diaLargo(iso));

export default function AgendaEmpresa({ pedidos = [], onAbrirPedido, onCreado }) {
    const [datos, setDatos] = useState(null);
    const [sumando, setSumando] = useState(null);
    const cargar = useCallback(() => api.get('/empresa/agenda', { params: { dias: 14 } })
        .then(r => setDatos(r.data)).catch(() => setDatos({ dias: [] })), []);
    useEffect(() => { cargar(); }, [cargar]);

    const sumar = async (dia, c) => {
        setSumando(c.sedeId);
        const { dow } = partes(dia.fecha);
        try {
            await api.post('/empresa/pedidos', {
                sedeId: c.sedeId, motivo: 'Mantenimiento / limpieza',
                equipos: c.series.map(serie => ({ serie, motivo: 'Mantenimiento / limpieza' })),
                ventanas: dow === 0 ? null : FRANJAS.map(franja => ({ dia: DIA_ID[dow], franja })),
                detalle: `Para aprovechar la visita del ${diaLargo(dia.fecha)} a ${c.cercaDe}.`,
            });
            toast.success(`Listo: pedimos el service de ${c.nombre}. Te avisamos cuando lo agendemos.`);
            cargar(); onCreado && onCreado();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo pedir'); }
        finally { setSumando(null); }
    };

    if (!datos) return <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-16 rounded-2xl bg-card animate-pulse" />)}</div>;
    if (!datos.dias.length) return (
        <div className="py-14 text-center space-y-1.5">
            <p className="text-body-lg font-black text-ink">No hay visitas agendadas</p>
            <p className="text-body text-muted">Cuando agendemos visitas a tus clientes en los próximos 14 días, las vas a ver acá.</p>
        </div>
    );
    return (
        <div className="space-y-4">
            <p className="px-1 text-caption text-muted">Las horas son aproximadas: el técnico sigue este orden. Con "Avisar" le mandás el día a tu cliente desde tu celu.</p>
            {datos.dias.map(dia => (
                <section key={dia.fecha} className="space-y-1.5">
                    <p className="px-1 text-label font-black uppercase tracking-widest text-muted">{tituloDia(dia.fecha)} <span className="opacity-70">· {dia.visitas.length}</span></p>
                    <div className="rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] divide-y divide-black/[0.06] dark:divide-white/[0.06] overflow-hidden">
                        {dia.visitas.map(v => {
                            const est = estadoDe(v);
                            const pedido = v.pedidoId ? pedidos.find(p => p.id === v.pedidoId) : null;
                            return (
                                <div key={v.id} className="flex items-center gap-3 px-4 py-3">
                                    <button type="button" onClick={() => pedido && onAbrirPedido && onAbrirPedido(pedido)} disabled={!pedido}
                                        className="flex-1 min-w-0 flex items-center gap-3 text-left">
                                        <span className="w-14 shrink-0 text-body font-black text-ink tabular-nums">{v.hora ? horaTexto(v.hora).replace(' h', '') : '—'}</span>
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-body font-black text-ink truncate">{v.lugar || v.direccion}</span>
                                            <span className="block text-caption text-muted truncate">{[v.tarea, v.tecnico].filter(Boolean).join(' · ')}</span>
                                            {v.estado !== 'AGENDADO' && (
                                                <span className="mt-0.5 inline-flex items-center gap-1 text-label font-black" style={{ color: est.color }}>
                                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: est.color }} />{est.label}
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                    {v.estado !== 'HECHO' && (
                                        <button type="button" onClick={() => compartir(mensajeAviso(v))} aria-label={`Avisarle a ${v.lugar || 'el cliente'}`}
                                            className="shrink-0 h-9 px-2.5 rounded-xl bg-chip text-label font-black text-ink inline-flex items-center gap-1 active:scale-95">
                                            <LuShare2 size={14} /> Avisar
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {dia.cerca.length > 0 && (
                        <div className="rounded-2xl border border-[color:var(--warning-tx)]/30 bg-[color:var(--warning-bg)] p-3 space-y-2">
                            <p className="text-caption font-black text-ink">
                                Tenés {dia.cerca.length === 1 ? 'un cliente' : `${dia.cerca.length} clientes`} cerca con el service por hacer. ¿Lo sumamos a este día?
                            </p>
                            {dia.cerca.map(c => (
                                <div key={c.sedeId} className="flex items-center gap-2 p-2.5 rounded-xl bg-card">
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-caption font-black text-ink truncate">{c.nombre}</span>
                                        <span className="flex items-center gap-1 text-label text-muted truncate">
                                            <LuMapPin size={11} className="shrink-0" />a {metros(c.metros)} de {c.cercaDe} · {c.series.length} eq. · {c.estado === 'VENCIDO' ? 'vencido' : 'por vencer'}
                                        </span>
                                    </span>
                                    <button type="button" onClick={() => sumar(dia, c)} disabled={sumando === c.sedeId}
                                        className="shrink-0 h-9 px-3 rounded-xl bg-brand-red text-white text-label font-black inline-flex items-center gap-1 disabled:opacity-50 active:scale-95">
                                        <LuPlus size={14} /> {sumando === c.sedeId ? 'Pidiendo…' : 'Sumar'}
                                    </button>
                                </div>
                            ))}
                            <p className="text-label text-muted">Te queda como pedido: lo agendamos en ese recorrido si hay lugar.</p>
                        </div>
                    )}
                </section>
            ))}
        </div>
    );
}
